/* eslint-disable no-console */
// Service Worker registration and update management

let registration = null
let updatePromptCallback = null

// Check if service workers are supported
export const isServiceWorkerSupported = () =>
  'serviceWorker' in navigator &&
  (window.location.protocol === 'https:' || window.location.hostname === 'localhost')

// Register the service worker
export const registerServiceWorker = async () => {
  if (!isServiceWorkerSupported()) {
    console.log('[SW Registration] Service Workers not supported or not HTTPS')
    return null
  }

  // Only register in production or when explicitly enabled
  const isProduction = import.meta.env.PROD
  const isEnabled = import.meta.env.VITE_SERVICE_WORKER_ENABLED === 'true'

  if (!isProduction && !isEnabled) {
    console.log('[SW Registration] Service Worker disabled in development')
    return null
  }

  try {
    console.log('[SW Registration] Registering Service Worker...')
    registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
    })

    console.log('[SW Registration] Service Worker registered successfully')

    // Check for updates every hour
    setInterval(
      () => {
        registration.update()
      },
      1000 * 60 * 60
    )

    // Handle updates
    registration.addEventListener('updatefound', handleUpdateFound)

    // Check if there's already a waiting worker (page was refreshed during update)
    if (registration.waiting) {
      handleWaitingWorker(registration.waiting)
    }

    return registration
  } catch (error) {
    console.error('[SW Registration] Service Worker registration failed:', error)
    return null
  }
}

// Unregister the service worker
export const unregisterServiceWorker = async () => {
  if (!isServiceWorkerSupported()) {
    return false
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map((reg) => reg.unregister()))
    console.log('[SW Registration] Service Worker unregistered successfully')
    return true
  } catch (error) {
    console.error('[SW Registration] Service Worker unregistration failed:', error)
    return false
  }
}

// Handle when a new service worker is found
function handleUpdateFound() {
  const newWorker = registration.installing
  console.log('[SW Update] New Service Worker found')

  newWorker.addEventListener('statechange', () => {
    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
      // New service worker is ready to activate
      handleWaitingWorker(newWorker)
    }
  })
}

// Handle waiting worker (ready to activate)
function handleWaitingWorker(worker) {
  console.log('[SW Update] New Service Worker waiting to activate')

  // Notify the app that an update is available
  if (updatePromptCallback) {
    updatePromptCallback(worker)
  }
}

// Set callback for update prompts
export const setUpdatePromptCallback = (callback) => {
  updatePromptCallback = callback
}

// Skip waiting and activate new service worker
export const skipWaiting = async () => {
  if (!registration?.waiting) {
    console.log('[SW Update] No waiting Service Worker')
    return false
  }

  try {
    // Tell the waiting service worker to skip waiting
    registration.waiting.postMessage({ type: 'SKIP_WAITING' })

    // Listen for the controlling service worker to change
    await new Promise((resolve) => {
      const onControllerChange = () => {
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
        resolve()
      }
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
    })

    // Reload the page to use the new service worker
    window.location.reload()
    return true
  } catch (error) {
    console.error('[SW Update] Failed to skip waiting:', error)
    return false
  }
}

// Get current service worker version
export const getServiceWorkerVersion = () =>
  new Promise((resolve, reject) => {
    if (!navigator.serviceWorker.controller) {
      reject(new Error('No active Service Worker'))
      return
    }

    const messageChannel = new MessageChannel()

    messageChannel.port1.onmessage = (event) => {
      if (event.data && event.data.version) {
        resolve(event.data.version)
      } else {
        reject(new Error('Invalid response from Service Worker'))
      }
    }

    navigator.serviceWorker.controller.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2])

    // Timeout after 5 seconds
    setTimeout(() => {
      reject(new Error('Service Worker version request timed out'))
    }, 5000)
  })

// Check if an update is available
export const checkForUpdate = async () => {
  if (!registration) {
    console.log('[SW Update] No Service Worker registration')
    return false
  }

  try {
    await registration.update()
    return registration.waiting !== null
  } catch (error) {
    console.error('[SW Update] Failed to check for update:', error)
    return false
  }
}

// Clear all caches (for debugging/troubleshooting)
export const clearAllCaches = async () => {
  if (!('caches' in window)) {
    console.log('[SW Cache] Caches API not supported')
    return false
  }

  try {
    const cacheNames = await caches.keys()
    await Promise.all(cacheNames.map((name) => caches.delete(name)))
    console.log('[SW Cache] All caches cleared successfully')
    return true
  } catch (error) {
    console.error('[SW Cache] Failed to clear caches:', error)
    return false
  }
}
